import createMapLayerRequestTracker from './createMapLayerRequestTracker';
import { getIntervalBoundaryKey } from './getIntervalBoundaryKey';
import { useSurveyForField } from '../useSurveyForField';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import PrescriptionSplitView from 'js/components/Prescription/PrescriptionSplitView/PrescriptionSplitView';
import { usePrescriptionJob } from 'js/components/Prescription/PrescriptionJobContext';
import { usePrescriptionJobStrategy } from '../PrescriptionJobStrategyContext';
import MapCanvas from '../../../components/MapObjects/MapCanvas/MapCanvas.jsx';

import {
  isSatelliteLayer,
  isSoilLayer,
  isSurveyLayer,
  isYieldLayer,
  PrescriptionJob,
} from 'js/components/Prescription/PrescriptionJob';

import { PRESCRIPTION_INTERVAL_COLORS } from 'js/constants/PrescriptionConstants';
import MapCanvasClickable, {
  canvasToMap,
} from 'js/components/MapObjects/MapCanvas/MapCanvasClickable';
import BulkNullValueCanvas from './BulkNullValueCanvas';
import MapFloatPanel from 'js/components/MapView/MapFloatPanel';
import PrescriptionOverrideList from 'js/components/Prescription/PrescriptionOverrideList/PrescriptionOverrideList';
import { connect, useStore } from 'react-redux';
import DataLayer from '../../DataLayer/DataLayer';
import FeaturePolygon from '../../DataLayer/FeaturePolygon';
import { yellow } from '@mui/material/colors';
import MapLegend from '../../MapLegend/MapLegend';
import WebAPIUtils from '../../../WebAPIUtils';
import { useFarm } from '../../../context/AccountContext';
import { useHookRef } from '../../../hooks/useHookRef';
import {
  calculateIntervalAreas,
  calculateLimeJobIntervals,
  getBulkBounds,
  getLayerConfig,
  initializeOverrides,
} from '../PrescriptionUtils';
import { Box, Tooltip, Typography } from '@mui/material';
import MapCanvasMiniatureContainer from '../../MapObjects/MapCanvas/MapCanvasMiniatureContainer';
import ViewModeConstants from '../../../constants/ViewModeConstants';
import HeightMapToggle from '../../MapObjects/HeightMap/HeightMapToggle';
import GoogleMap from '../../MapObjects/GoogleMap/GoogleMap';
import HeightMap from '../../MapObjects/HeightMap/HeightMap';
import useFirebaseAnalytics, { FIREBASE_EVENTS } from '../../../hooks/useFirebaseAnalytics';
import * as turf from '@turf/turf';
import { iterateRawValues, iterateValidOverrides } from '../../../helpers/SurveyUtils';
import { useSelectedSurvey, useSurveyLayerViewCapabilities } from '../../../context/SurveyContext';
import {
  Action,
  useActionSnackbarContext,
} from '../../ActionSnackbarHandler/ActionSnackbarHandler';
import { useLangFile } from '../../../context/LanguageContext';
import { getClassificationValues } from '../../../reducers/SurveyReducer';
import {
  getDemandTypeForLayer,
  isDemandLayerType,
} from '../../../model/surveys/country/SurveyCountryStrategy';
import {
  getAllDemandMiniatures,
  getDefaultDemandMiniatures,
} from '../../../helpers/DemandMiniatureDefaults';
import { useSurveyCountryCode } from '../../../hooks/useSurveyCountryCode';
import { DATA_INTEGRATION_YIELD, YIELD_LAYERS } from '../../../constants/YieldLayer';
import YieldSelector from './YieldSelector';
import VariableColorScale from '../../MapLegend/VariableColorScale';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Button from '@mui/material/Button';
import { useMultipleJobs } from '../PrescriptionJobContext.js';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { MetaTypes } from '../PrescriptionJob.js';
import { SURVEY_LAYERS } from '../../../constants/SurveyLayers.js';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrescriptionRecalculateDialog from '../Dialogs/PrescriptionRecalculateDialog';
import { SATELLITE_LAYERS } from '../../../constants/SatelliteLayers.js';
import { usePrescriptionEditorContext } from '../PrescriptionEditorContext.js';
import DateHandler from '../../DateHandler/DateHandler.js';
import { fetchCropHealth } from '../../../reducers/FieldReducer.js';
import MapFieldHole from '../../MapObjects/MapFieldHole';
import MapPopup from '../../MapObjects/MapPopup';
import {
  getBrushablePixels,
  getNullValueRegions,
  getPrescriptionIntervalColor,
  hasSignificantNullZonesInField,
  isCellInsideField,
  isNullInterval,
  NULL_INTERVAL_VALUE,
  restoreNullValues,
} from '../nullValueRegions';
import { DefaultLimePrescriptionStrategy } from '../LimeJob/LimeJobStrategies/DefaultLimePrescriptionStrategy';
import { setNullIntervalForJob, setNullIntervalInIntervals } from '../nullIntervalJob';
import {
  formatDateAsYearMonthDate,
  formatDateAsYearMonthDateTime,
} from '../../../helpers/DateUtils';
import moment from 'moment/moment';
import debounce from 'lodash.debounce';
import {
  resetBulkPrescriptionFutureJob,
  resetFutureJob,
  setBulkPrescriptionJob,
  setBulkPrescriptionJobs,
} from '../../../reducers/PrescriptionReducer';
import { fitMapToBounds } from '../../../helpers/MapsUtils';
import { getSurveysForSelectedFields } from '../../../helpers/surveysForSelectedFields';
import { hideProgress } from '../../../reducers/ProgressReducer';
import { NORMALIZED_IMAGES_KEY, RAW_IMAGES_KEY } from '../../../model/surveys/SurveyImageMapping';
import { getPrescriptionSatelliteImageUrl } from './getPrescriptionSatelliteImageUrl';
import { getPrescriptionLegendValues } from './getPrescriptionLegendValues';
import { Identifier } from '../../../model/prescription/PrescriptionModelReference';
import { MultiLayerSeedingPrescriptionStrategyV1 } from '../SeedingJob/SeedingJobStrategies/MultiLayerSeedingPrescriptionStrategyV1';
import { useBulkDemandClassifications } from '../useBulkDemandClassifications';
import {
  getDemandMapUpdates,
  getJobDemandChanges,
  recalculateJobIntervals,
} from '../prescriptionDemandMode';
import { usePrescriptionJobOrchestration } from '../SeedingJob/usePrescriptionJobOrchestration';
import { PrescriptionState } from '../PrescriptionState';

const tinycolor = require('tinycolor2');

const boundsToPolygon = (bounds) => {
  return [
    [bounds.west, bounds.north],
    [bounds.east, bounds.north],
    [bounds.east, bounds.south],
    [bounds.west, bounds.south],
    // end in the start point
    [bounds.west, bounds.north],
  ];
};

const validJob = (job: PrescriptionJob) => {
  const invalidIntervals = job?.intervals.filter((interval) => {
    return (
      Number.isNaN(interval.min) ||
      Number.isNaN(interval.max) ||
      !Number.isFinite(interval.min) ||
      !Number.isFinite(interval.max)
    );
  });

  return invalidIntervals?.length <= 0;
};

const RGB_COLORS = PRESCRIPTION_INTERVAL_COLORS.map((hex) => tinycolor(hex).toRgb());

export function getIntervalColorFromPixel(value, intervals, intervalColors) {
  let color;
  for (let i = 0; i < intervals.length; i++) {
    const interval = intervals[i];
    if (value >= interval.min && value <= interval.max) {
      color = intervalColors
        ? intervalColors[i]
        : tinycolor(getPrescriptionIntervalColor(interval, i, intervals)).toRgb();
      break;
    }
  }
  return color;
}

export function getIntervalColorForClassificationIndex(index) {
  let color;
  if (index < RGB_COLORS.length) {
    color = RGB_COLORS[index];
  }
  return color;
}

function setPixel(imageData, x, y, color) {
  const { r, g, b, a } = color;
  const index = (x + y * imageData.width) * 4;
  imageData.data[index] = r;
  imageData.data[index + 1] = g;
  imageData.data[index + 2] = b;
  imageData.data[index + 3] = Math.floor(a * 255);
}

const mapStateToProps = (store) => ({
  fields: store.field.fields,
  selectedFieldDates: store.field.selectedFieldDates,
  images: store.field.images,
  viewMode: store.control.viewMode,
  overlays: store.overlay.overlays,
  selectedSurveyClassificationValues: store.survey.selectedSurveyClassificationValues,
  dataIntegrations: store.integrations.dataIntegrations,
  storeSurveys: store.survey.surveys,
  isBulkPrescription: store.prescription.isBulkPrescription,
  appliedInitialIntervals: store.prescription.bulkPrescription.appliedInitialIntervals,
  bulkPrescriptionJobs: store.prescription.bulkPrescription.currentJobs,
  bulkFuturePrescriptionJobs: store.prescription.bulkPrescription.futureJobs,
  bulkPrescriptionFields: store.prescription.bulkPrescription.fields,
  bulkPrescriptionPrefix: store.prescription.bulkPrescription.prefix,
  selectedSurveyReferenceValues: store.survey.selectedSurveyReferenceValues,
  selectedLayer: store.survey.selectedLayer,
});

const PrescriptionSplitViewContainer = ({
  dispatch,
  isOverriding,
  fields,
  selectedField,
  viewMode,
  selectedFieldDates,
  overrideBrushValue,
  overrideBrushSize,
  onOverridesChanged,
  enableSurveys,
  enableNdvi,
  overlays,
  selectedSurveyClassificationValues,
  dataIntegrations,
  updatePrescriptionJob,
  numberOfAddedViews,
  setFutureJobAsFutureJob,
  storeSurveys,
  isBulkPrescription,
  appliedInitialIntervals,
  bulkPrescriptionJobs,
  bulkFuturePrescriptionJobs,
  bulkPrescriptionFields,
  selectedSurveyReferenceValues,
  selectedLayer,
  setShowLoading,
  onRecalculateBulkOverview,
  highlightNullZones,
}) => {
  const LangFile = useLangFile();
  const store = useStore();
  const { getSurveyForFieldAndLayer } = useSurveyForField();
  const analytics = useFirebaseAnalytics();
  const countryCode = useSurveyCountryCode();
  const { prescriptionJob } = usePrescriptionJob();
  const { addAction } = useActionSnackbarContext();
  const { showCurrentJob, hasMultipleJobs } = useMultipleJobs();
  const [shouldShowCurrentJob, setShowCurrentJob] = showCurrentJob;
  const selectedSurvey = useSelectedSurvey();
  const prescriptionJobStrategy = usePrescriptionJobStrategy();
  const { prescriptionState } = usePrescriptionJobOrchestration();

  const {
    mapsState,
    updateMapState,
    updateMapsState,
    selectedMap,
    datesWithImages,
    setMapPrescriptionBasedOn,
  } = usePrescriptionEditorContext();
  const storeSurveysRef = useHookRef(storeSurveys);
  const [showHeightMapLeft, setShowHeightMapLeft] = useState(false);
  const jobBasedOnOldField = prescriptionJob.usingJobPolygon;
  const activeField = useMemo(
    () =>
      jobBasedOnOldField
        ? {
            polygon: prescriptionJob.polygon,
            bounds: prescriptionJob.bounds,
            size: prescriptionJob.fieldSize,
            fieldId: prescriptionJob.fieldId,
          }
        : fields.get(prescriptionJob.fieldId) ||
          (selectedField?.fieldId === prescriptionJob.fieldId ? selectedField : null),
    [
      jobBasedOnOldField,
      prescriptionJob.polygon,
      prescriptionJob.bounds,
      prescriptionJob.fieldSize,
      prescriptionJob.fieldId,
      fields,
      selectedField,
    ]
  );
  const tabValue = shouldShowCurrentJob ? 0 : 1;

  const farm = useFarm();
  const farmId = useHookRef(farm.farmId);
  const legendRefLeftMap = useRef();
  const legendRefCenterMap = useRef();
  const prescriptionJobBoundsRef = useRef(prescriptionJob.bounds);
  const field = useRef(activeField);
  const jobPolygon = useRef(field.current.polygon);
  // Map layers read these refs during render. Keep them aligned with the job
  // before rendering a newly selected bulk field.
  field.current = activeField;
  jobPolygon.current = activeField.polygon;
  prescriptionJobBoundsRef.current = activeField.bounds;
  const visibleFieldHoles = jobPolygon.current.coordinates.slice(1);
  const referenceValuesFetchMeta = useRef({});

  const clickPolygon = useMemo(() => boundsToPolygon(activeField.bounds), [activeField.bounds]);

  const isOnBulkOverview = useMemo(() => {
    return prescriptionState === PrescriptionState.BULK_PRESCRIPTION_OVERVIEW;
  }, [prescriptionState]);

  const getInitialMapBounds = useCallback(() => {
    return isOnBulkOverview
      ? getBulkBounds(bulkPrescriptionFields)
      : prescriptionJobBoundsRef.current;
  }, [isOnBulkOverview, bulkPrescriptionFields, prescriptionJobBoundsRef]);

  const surveysForSelectedFields = useMemo(() => {
    return getSurveysForSelectedFields(
      storeSurveys,
      bulkPrescriptionFields,
      isOnBulkOverview,
      prescriptionJob.fieldId
    );
  }, [storeSurveys, bulkPrescriptionFields, isOnBulkOverview, prescriptionJob.fieldId]);

  const [sourceValues, setSourceValues] = useState(prescriptionJob.values);
  const [canvasSize, setCanvasSize] = useState({
    height: sourceValues.length,
    width: sourceValues[0].length,
  });

  const [showHeightMapRight, setShowHeightMapRight] = useState(false);
  const [shouldScaleLatitude, setShouldScaleLatitude] = useState(false);
  const [variableColorScaleObject, setVariableColorScaleObject] = useState();
  const [showRecalculateDialog, setShowRecalculateDialog] = useState(false);
  const [initializationCompleted, setInitializationCompleted] = useState(false);
  const [auxValues, setAuxValues] = useState({ leftMap: {}, centerMap: {} });
  const bulkDemandClassificationsByLayer = useBulkDemandClassifications(
    isOnBulkOverview,
    mapsState.leftMap,
    mapsState.centerMap
  );

  const leftGoogleMapRef = useRef(null);
  const centerGoogleMapRef = useRef(null);
  const rightGoogleMapRef = useRef(null);

  // milliseconds
  const debounceDelay = 140;

  const metaTypeToInitialLayerSelection = {
    [MetaTypes.FERTILIZING]: [],
    [MetaTypes.SEEDING]: [],
    [MetaTypes.LIME]: [SURVEY_LAYERS.HUMUS, SURVEY_LAYERS.CLAY],
    [MetaTypes.SPRAYING]: [],
    [MetaTypes.SPOT_SPRAYING]: [],
    [MetaTypes.LEGACY]: [],
  };

  const enableYield = dataIntegrations.some((d) => {
    return DATA_INTEGRATION_YIELD.includes(d.name.toLowerCase()) && d.connected;
  });

  const layerCapabilities = useSurveyLayerViewCapabilities(
    Object.values(mapsState).find((map) => map.identifier === selectedMap).sourceLayer,
    ViewModeConstants.PRESCRIPTION
  );

  const disableClassificationToggle =
    prescriptionJob.jobId &&
    prescriptionJob.layerType &&
    layerCapabilities.enableCustomClassification;

  const beginLayerRequest = useRef(createMapLayerRequestTracker()).current;

  // Recalculation changes the job's data/source, not the user's comparison maps.
  // Only initialize map selections when entering a field or changing job settings.
  const initializedMapSelection = useRef(null);
  const preserveSelectionsOnRecalculation = useRef(false);
  const mapSelectionInitializationKey = JSON.stringify([
    prescriptionJob.fieldId,
    prescriptionJob.layer,
    prescriptionJob.metaType,
    prescriptionJob.maxPrescription,
    prescriptionJob.prescriptionModel?.identifier,
    prescriptionJob.prescriptionModel?.version,
  ]);
  const shouldInitializeMapSelection =
    !preserveSelectionsOnRecalculation.current &&
    initializedMapSelection.current !== mapSelectionInitializationKey;

  const handleTabChange = (tabValue) => {
    const tabToJob = {
      0: true,
      1: false,
    };

    setShowCurrentJob(tabToJob[tabValue]);
  };

  const findMatchingSurveys = (sourceLayer, selectedDate, selectedFieldId, surveyImageId) => {
    if (isSatelliteLayer(sourceLayer)) {
      return storeSurveysRef.current[0];
    }

    if (jobBasedOnOldField) {
      return selectedSurvey;
    }

    return (
      (selectedDate && getSurveyForFieldAndLayer(selectedFieldId, sourceLayer, selectedDate)) ||
      getSurveyForFieldAndLayer(selectedFieldId, sourceLayer, undefined, surveyImageId) ||
      getSurveyForFieldAndLayer(selectedFieldId, sourceLayer)
    );
  };

  const debouncedUpdateLeftMap = useRef(
    debounce((mapData, prescriptionJob, surveysForSelectedFields) => {
      const mapName = 'leftMap';
      handleLayer(mapName, mapData, prescriptionJob);
      getReferenceValues(mapName, mapData, prescriptionJob, surveysForSelectedFields);
    }, debounceDelay)
  ).current;

  const debouncedUpdateCenterMap = useRef(
    debounce((mapData, prescriptionJob, surveysForSelectedFields) => {
      const mapName = 'centerMap';
      handleLayer(mapName, mapData, prescriptionJob);
      getReferenceValues(mapName, mapData, prescriptionJob, surveysForSelectedFields);
    }, debounceDelay)
  ).current;

  const handleLayer = useCallback(
    (mapName, mapData, prescriptionJob) => {
      const { sourceLayer, classificationsEnabled, variationsEnabled, selectedDate } = mapData;
      const isCurrentRequest = beginLayerRequest(mapName);

      if (isSurveyLayer(sourceLayer)) {
        if (jobBasedOnOldField && sourceLayer !== prescriptionJob.layer) {
          updateMapState(mapName, 'sourceLayer', prescriptionJob.layer);
          return;
        }

        const selectedDateSurvey = findMatchingSurveys(
          sourceLayer,
          mapData.mapSurvey?.fieldId === prescriptionJob.fieldId ? selectedDate : undefined,
          prescriptionJob.fieldId,
          prescriptionJob.surveyImageId
        );

        if (selectedDateSurvey === undefined) {
          updateMapsState({ [mapName]: { mapSurvey: null, imageUrl: null } });
          return;
        }

        const config = getLayerConfig(sourceLayer);
        const url = config.getImageUrl(
          selectedDateSurvey.newImages,
          classificationsEnabled,
          !isSoilLayer(sourceLayer) && variationsEnabled && !classificationsEnabled,
          ViewModeConstants.PRESCRIPTION
        );

        const surveyDate = formatDateAsYearMonthDateTime(selectedDateSurvey.publishDate);
        const mapUpdates = { mapSurvey: selectedDateSurvey, imageUrl: url };
        if (surveyDate !== selectedDate) {
          mapUpdates.selectedDate = surveyDate;
        }
        updateMapsState({ [mapName]: mapUpdates });
        setShouldScaleLatitude(true);
      } else if (isSatelliteLayer(sourceLayer)) {
        if (prescriptionJob && prescriptionJob.assets && selectedDate) {
          if (!datesWithImages.includes(selectedDate)) {
            updateMapState(mapName, 'selectedDate', datesWithImages[datesWithImages.length - 1]);
            return;
          }

          const requestedFieldId = field.current.fieldId;
          dispatch(fetchCropHealth(farmId.current, selectedDate, fields)).then((result) => {
            if (!isCurrentRequest() || field.current.fieldId !== requestedFieldId) return;
            const fieldCropHealthObject = result.value.find(
              (item) => item.fieldId === requestedFieldId
            );
            if (!fieldCropHealthObject) return;
            updateMapState(mapName, 'imageUrl', fieldCropHealthObject[sourceLayer]);
            setShouldScaleLatitude(false);
          });
        }
      }
    },
    [field.current.fieldId, farmId.current]
  );

  const needsReferenceFetch = ({
    referenceValues,
    selectedDate,
    imageUrl,
    mapSurvey,
    sourceLayer,
    isSatellite,
    isSurvey,
    contextHash,
    prevContextHash,
    multipleSelections,
  }) => {
    const isReferenceValueMissing = referenceValues[sourceLayer] == null;

    const areAnyMultipleSelectionsMissing = multipleSelections.some(
      (layer) => referenceValues[layer] == null
    );

    const isSelectedDateInImageUrl = imageUrl?.includes(selectedDate) || false;
    const isSelectedDateInPublishDate = mapSurvey?.publishDate?.includes(selectedDate);
    const selectedDateInImages =
      selectedDate && datesWithImages !== null && datesWithImages.includes(selectedDate);

    const isDateMismatch =
      (!isSelectedDateInImageUrl && isSatellite && selectedDateInImages) ||
      (!isSelectedDateInPublishDate && isSurvey);

    const isAnyReferenceMissing = isReferenceValueMissing || areAnyMultipleSelectionsMissing;

    const isImageDataAvailable = selectedDateInImages || !isSatellite;

    const contextChanged = contextHash !== prevContextHash;
    return (
      ((isAnyReferenceMissing && isImageDataAvailable) || (isDateMismatch && imageUrl)) &&
      contextChanged
    );
  };

  const removeSourceLayerFromSelections = (
    mapName,
    sourceLayer,
    referenceValues,
    multipleSelections
  ) => {
    const filtered = Object.keys(referenceValues).filter(
      (layer) => layer !== sourceLayer && multipleSelections.includes(layer)
    );

    updateMapState(mapName, 'multipleSelections', filtered);
  };

  const shouldFetchClassifications = (classificationsEnabled, sourceLayer) =>
    classificationsEnabled && getDemandTypeForLayer(sourceLayer, countryCode) != null;

  const fetchMissingClassifications = (
    sourceLayer,
    multipleSelections,
    selectedDate,
    mapSurvey
  ) => {
    const appliedInitialIntervals =
      store.getState().prescription.bulkPrescription.appliedInitialIntervals;

    if (
      selectedSurveyClassificationValues[sourceLayer] == null &&
      !(isBulkPrescription && !appliedInitialIntervals)
    ) {
      setShowLoading(true);
      dispatch(
        getClassificationValues(
          farmId.current,
          field.current.fieldId,
          selectedDate,
          sourceLayer,
          mapSurvey,
          prescriptionJob.jobId
        )
      );
    }

    const demandMiniatureLayers = getDefaultDemandMiniatures(sourceLayer, countryCode);
    multipleSelections.forEach((layer) => {
      if (getDemandTypeForLayer(layer, countryCode) == null) {
        return;
      }
      if (demandMiniatureLayers.includes(layer)) {
        return;
      }
      if (selectedSurveyClassificationValues[layer] == null) {
        dispatch(
          getClassificationValues(
            farmId.current,
            field.current.fieldId,
            selectedDate,
            layer,
            mapSurvey,
            prescriptionJob.jobId
          )
        );
      }
    });
  };

  const getReferenceValuesForMultipleLayersPromise = async (
    mapName,
    sourceLayer,
    multipleSelections,
    surveys,
    fieldId
  ) => {
    const map = mapsState[mapName];

    if (isSatelliteLayer(sourceLayer) && jobBasedOnOldField) {
      const result = { [sourceLayer]: prescriptionJob.values };
      multipleSelections.forEach((layer) => {
        result[layer] = prescriptionJob.values;
      });

      return result;
    }

    const valuesArray = await WebAPIUtils.getMultipleLayerValues(
      farmId.current,
      fieldId,
      map.selectedDate,
      surveys,
      [sourceLayer, ...multipleSelections],
      prescriptionJob.jobId,
      jobBasedOnOldField
    );

    if (valuesArray != null && multipleSelections.length + 1 === valuesArray.length) {
      const result = { [sourceLayer]: valuesArray[0] };
      multipleSelections.forEach((layer, index) => {
        result[layer] = valuesArray[index + 1];
      });
      return result;
    }

    return null;
  };

  const getReferenceValuesForSingleLayerPromise = async (
    mapName,
    selectedDate,
    sourceLayer,
    selectedDateSurvey,
    fieldId
  ) => {
    let values;
    if (jobBasedOnOldField) {
      if (isSatelliteLayer(sourceLayer)) {
        values = prescriptionJob.values;
      } else if (isSurveyLayer(sourceLayer)) {
        const result = await WebAPIUtils.getLayerValuesFromS3(
          farmId.current,
          prescriptionJob.jobId,
          sourceLayer
        );
        values = result;
      }
    } else {
      values = await WebAPIUtils.getLayerValues(
        farmId.current,
        fieldId,
        selectedDate,
        sourceLayer,
        selectedDateSurvey,
        prescriptionJob.jobId,
        null,
        prescriptionJob.layer
      );
    }

    if (values != null) {
      return {
        [sourceLayer]: values,
      };
    }

    if (isYieldLayer(sourceLayer)) {
      return;
    }

    const layerName = getLayerConfig(sourceLayer).getName(LangFile);
    addAction(
      new Action(
        `failed-to-load-values-${sourceLayer}`,
        LangFile.PrescriptionContainer.failedToLoadValues.replace('%s', layerName),
        'warning',
        'filled'
      )
    );
  };

  const getDkRtAuxiliaryValuesPromise = async (selectedDate, selectedDateSurvey) => {
    const auxLayers = [SURVEY_LAYERS.RT_DESIRED, SURVEY_LAYERS.RT_DIFF];
    const results = await Promise.all(
      auxLayers.map((layer) =>
        WebAPIUtils.getLayerValues(
          farmId.current,
          field.current.fieldId,
          selectedDate,
          layer,
          selectedDateSurvey,
          prescriptionJob.jobId,
          null,
          prescriptionJob.layer
        )
          .then((values) => ({ layer, values }))
          .catch(() => ({ layer, values: null }))
      )
    );

    const out = {};
    results.forEach(({ layer, values }) => {
      if (values != null) out[layer] = values;
    });
    return out;
  };

  const fetchAndUpdateReferenceValues = async ({
    mapName,
    selectedDate,
    sourceLayer,
    referenceValues,
    multipleSelections,
    contextHash,
  }) => {
    const referenceValuesForField = {};

    const fieldsToGetReferenceValues = isBulkPrescription
      ? bulkPrescriptionFields
      : [field.current];

    const results = await Promise.allSettled(
      fieldsToGetReferenceValues.map(async (field) => {
        const surveyForField =
          storeSurveysRef.current.find(
            (survey) =>
              survey.fieldId === field.fieldId &&
              formatDateAsYearMonthDateTime(survey.publishDate) === selectedDate &&
              Object.keys(survey.newImages).includes(sourceLayer)
          ) || storeSurveysRef.current.find((survey) => survey.fieldId === field.fieldId);

        const val =
          multipleSelections.length > 0
            ? await getReferenceValuesForMultipleLayersPromise(
                mapName,
                sourceLayer,
                multipleSelections,
                storeSurveysRef.current,
                field.fieldId
              )
            : await getReferenceValuesForSingleLayerPromise(
                mapName,
                selectedDate,
                sourceLayer,
                surveyForField,
                field.fieldId
              );

        return [String(field.fieldId), val];
      })
    );

    for (const r of results) {
      if (r.status === 'fulfilled' && Array.isArray(r.value)) {
        const [id, val] = r.value;
        // Loaders resolve to null or undefined when they have nothing for the field, e.g. yield,
        // whose values the YieldSelector loads itself.
        if (val != null) referenceValuesForField[id] = val;
      } else if (r.status === 'rejected') {
        console.warn('Reference value fetch failed for a field', r.reason);
      }
    }

    if (Object.keys(referenceValuesForField).length > 0) {
      const newReferenceValues = { ...referenceValues, ...referenceValuesForField };
      // only select valid/loaded layers
      const selectedLayers = Object.keys(newReferenceValues[field.current.fieldId] || {}).filter(
        (layer) => layer !== sourceLayer && multipleSelections.includes(layer)
      );

      if (
        multipleSelections.includes(YIELD_LAYERS.YIELD) &&
        !selectedLayers.includes(YIELD_LAYERS.YIELD)
      ) {
        const index = multipleSelections.indexOf(YIELD_LAYERS.YIELD);
        selectedLayers.splice(index, 0, YIELD_LAYERS.YIELD);
      }

      updateMapState(mapName, 'referenceValues', newReferenceValues);
      updateMapState(mapName, 'multipleSelections', selectedLayers);

      referenceValuesFetchMeta.current[sourceLayer] = {
        contextHash,
        lastUpdated: Date.now(),
      };
    }
  };

  const getReferenceValues = async (
    mapName,
    mapData,
    prescriptionJob,
    surveysForSelectedFields
  ) => {
    const {
      referenceValues,
      sourceLayer,
      imageUrl,
      selectedDate,
      classificationsEnabled,
      multipleSelections,
      mapSurvey,
    } = mapData;

    const contextHash = `${mapName}-${selectedDate}-${[sourceLayer, ...multipleSelections].join(',')}`;
    const prevContextHash = referenceValuesFetchMeta.current[sourceLayer]?.contextHash;

    const isSatellite = isSatelliteLayer(sourceLayer);
    const isSurvey = isSurveyLayer(sourceLayer);

    const shouldFetchReferenceValues = needsReferenceFetch({
      referenceValues,
      selectedDate,
      imageUrl,
      mapSurvey,
      sourceLayer,
      isSatellite,
      isSurvey,
      contextHash,
      prevContextHash,
      multipleSelections,
    });

    if (shouldFetchReferenceValues) {
      await fetchAndUpdateReferenceValues({
        mapName,
        selectedDate,
        sourceLayer,
        referenceValues,
        multipleSelections,
        contextHash,
      });
      dispatch(hideProgress());
    } else if (multipleSelections.includes(sourceLayer)) {
      removeSourceLayerFromSelections(mapName, sourceLayer, referenceValues, multipleSelections);
    }

    if (shouldFetchClassifications(classificationsEnabled, sourceLayer)) {
      fetchMissingClassifications(sourceLayer, multipleSelections, selectedDate, mapSurvey);
    }
  };

  const syncDemandMiniatures = useCallback(
    (mapName, map) => {
      const allMiniatures = getAllDemandMiniatures(countryCode);
      const newMiniatures = getDefaultDemandMiniatures(map.sourceLayer, countryCode);
      const baseSelections = (map.multipleSelections || []).filter(
        (layer) => !allMiniatures.includes(layer)
      );
      const toAdd = newMiniatures.filter((layer) => !baseSelections.includes(layer));
      const synced = [...baseSelections, ...toAdd];

      const current = map.multipleSelections || [];
      const changed = synced.length !== current.length || synced.some((l, i) => l !== current[i]);
      if (changed) {
        updateMapState(mapName, 'multipleSelections', synced);
      }
    },
    [countryCode, updateMapState]
  );

  const setChangeDemandClassification = (mapName, change) => {
    const map = mapsState[mapName];
    const baseline =
      prescriptionJobStrategy?.miniatureMapSelection ||
      metaTypeToInitialLayerSelection[prescriptionJob.metaType] ||
      [];
    updateMapsState({
      [mapName]: getDemandMapUpdates(map, change, countryCode, baseline),
    });
  };

  const recalculateLimeJobPrescription = (job, map, fieldSize) => {
    const strategy = new DefaultLimePrescriptionStrategy();

    const limeValues = strategy.calculateLimePrescription(
      map.referenceValues[job.fieldId][SURVEY_LAYERS.HUMUS],
      map.referenceValues[job.fieldId][SURVEY_LAYERS.CLAY],
      map.referenceValues[job.fieldId][SURVEY_LAYERS.RT],
      job.maxPrescription / fieldSize,
      job.limeInfo.tolerance,
      job.limeInfo.efficiency
    );

    const bulkJobExistsForField = isBulkPrescription && bulkPrescriptionJobs[job.fieldId];

    const currentJob = {
      ...job,
      values: bulkJobExistsForField
        ? restoreNullValues(bulkPrescriptionJobs[job.fieldId].values)
        : limeValues.current,
      intervals: bulkJobExistsForField
        ? bulkPrescriptionJobs[job.fieldId].intervals
        : calculateLimeJobIntervals(limeValues.current),
    };
    const finalizedCurrentJob = {
      ...calculateIntervalAreas(currentJob),
      overrides: initializeOverrides(currentJob),
    };

    const vrmSet = prescriptionJob.vrmSet;
    const futureJobVrmSet = vrmSet != null ? vrmSet : { jobIndex: 1 };

    const futureJob = {
      ...finalizedCurrentJob,
      originalValues: limeValues.current,
      values: limeValues.future,
      jobName: finalizedCurrentJob.jobName + ` - ${LangFile.PrescriptionEditorContainer.future}`,
      date: new Date(
        new Date(finalizedCurrentJob.date).setFullYear(finalizedCurrentJob.date.getFullYear() + 1)
      ),
      vrmSet: futureJobVrmSet,
      intervals: bulkJobExistsForField
        ? bulkFuturePrescriptionJobs[job.fieldId]?.intervals
        : calculateLimeJobIntervals(limeValues.future),
    };

    let finalizedFutureJob = null;
    if (futureJob.intervals) {
      finalizedFutureJob = {
        ...calculateIntervalAreas(futureJob),
        overrides: initializeOverrides(futureJob),
      };
    }

    return { finalizedCurrentJob, finalizedFutureJob };
  };

  const recalculatePrescription = () => {
    const fieldId = field.current.fieldId;
    const map = Object.values(mapsState).find((map) => map.identifier === selectedMap);
    if (isOnBulkOverview) {
      preserveSelectionsOnRecalculation.current = true;
      onRecalculateBulkOverview(map, bulkDemandClassificationsByLayer[map.sourceLayer] || {});
      return;
    }
    const layer = map.sourceLayer;
    const getValues = () => {
      if (map.referenceValues[fieldId] && map.referenceValues[fieldId][layer]) {
        return map.referenceValues[fieldId][layer];
      }
      if (map.referenceValues[layer]) {
        return map.referenceValues[layer];
      }
      return map.referenceValues;
    };

    const referenceValues = getValues();
    if (!Array.isArray(referenceValues) && layer !== prescriptionJob.layer) {
      return;
    }
    let values = Array.isArray(referenceValues) ? referenceValues : prescriptionJob.values;

    if (!values || values.length === 0 || !values[0]) {
      return;
    }
    values = restoreNullValues(values);

    setSourceValues(values);
    setCanvasSize({
      height: values.length,
      width: values[0].length,
    });

    const preserveOriginalLayer =
      isBulkPrescription &&
      prescriptionJob.prescriptionModel?.identifier === Identifier.MultiLayerSeedingModel;
    let newJob = {
      ...prescriptionJob,
      values: values,
      date: new Date(map.selectedDate),
      ...getJobDemandChanges(map, prescriptionJob, countryCode, selectedSurveyClassificationValues),
      layer: preserveOriginalLayer ? prescriptionJob.layer : map.sourceLayer,
    };

    if (!isBulkPrescription) {
      newJob.jobId = null;
      newJob.saved = false;
    }

    if (map.mapSurvey) {
      newJob.surveyImageId = map.mapSurvey.surveyImageId;
    }

    if (isBulkPrescription) {
      newJob.jobName = bulkPrescriptionJobs[newJob.fieldId].jobName;
    }

    if (newJob.metaType === MetaTypes.LIME && isDemandLayerType(newJob.layerType)) {
      newJob = recalculateJobIntervals(newJob);
      // Demand rates are chosen manually, so the numeric lime remainder no longer applies.
      dispatch(resetFutureJob());
      if (isBulkPrescription) {
        dispatch(resetBulkPrescriptionFutureJob(fieldId));
      }
    } else if (newJob.metaType === MetaTypes.LIME) {
      const { finalizedCurrentJob, finalizedFutureJob } = recalculateLimeJobPrescription(
        newJob,
        map,
        field.current.size
      );
      if (validJob(finalizedFutureJob)) {
        setFutureJobAsFutureJob(finalizedFutureJob);
      } else {
        dispatch(resetFutureJob());
      }

      newJob = finalizedCurrentJob;
    } else if (
      prescriptionJobStrategy instanceof MultiLayerSeedingPrescriptionStrategyV1 &&
      isBulkPrescription
    ) {
      newJob = prescriptionJobStrategy.initializeSingleJob(
        prescriptionJob,
        restoreNullValues(prescriptionJob.values),
        newJob.layerValues
      );
    } else {
      newJob = recalculateJobIntervals(newJob);
    }

    if (isBulkPrescription) {
      newJob.forceRecalculated = true;
    }

    setMapPrescriptionBasedOn(map.identifier);
    if (validJob(newJob)) {
      preserveSelectionsOnRecalculation.current = true;
      updatePrescriptionJob(newJob, true);
    }
  };

  const recalculatePrescriptionDisabled = () => {
    if (
      jobBasedOnOldField ||
      (!isBulkPrescription &&
        prescriptionJob.prescriptionModel?.identifier === Identifier.MultiLayerSeedingModel)
    )
      return true;

    const isLimeJob = prescriptionJob.metaType === MetaTypes.LIME;
    const map = Object.values(mapsState).find((map) => map.identifier === selectedMap);

    if (
      map.classificationsEnabled &&
      isOnBulkOverview &&
      bulkPrescriptionFields.some(
        (field) => !bulkDemandClassificationsByLayer[map.sourceLayer]?.[field.fieldId]
      )
    ) {
      return true;
    }
    if (
      map.classificationsEnabled &&
      !isOnBulkOverview &&
      !selectedSurveyClassificationValues[map.sourceLayer] &&
      !(prescriptionJob.layer === map.sourceLayer && prescriptionJob.classifications)
    ) {
      return true;
    }

    if (map.classificationsEnabled !== isDemandLayerType(prescriptionJob.layerType)) {
      const referenceValues =
        map.referenceValues[prescriptionJob.fieldId]?.[map.sourceLayer] ||
        map.referenceValues[map.sourceLayer];
      if (map.sourceLayer !== prescriptionJob.layer && !Array.isArray(referenceValues)) {
        return true;
      }
      return false;
    }

    if (isOnBulkOverview) {
      return true;
    }

    if (prescriptionState === PrescriptionState.BULK_PRESCRIPTION_SINGLE_FIELD_VIEW) {
      if (isSoilLayer(prescriptionJob.layer) || isLimeJob) {
        return true;
      }

      if (prescriptionJob.forceRecalculated) {
        return true;
      }

      if (prescriptionJob.layer !== map.sourceLayer) {
        return true;
      }

      return false;
    }

    const mapDate = moment(map.selectedDate);

    const isVitalityNdviSatellite =
      isSatelliteLayer(map.sourceLayer) && map.sourceLayer === SATELLITE_LAYERS.VITALITY_NDVI;

    const isNonLimeVitalityNdvi = isVitalityNdviSatellite && !isLimeJob;

    const isSurveyWithMatchingLayer =
      isSurveyLayer(map.sourceLayer) && prescriptionJob.layer === map.sourceLayer;

    let jobDate = moment(prescriptionJob.date);
    if (prescriptionJob.vrmSet?.jobIndex === 1) {
      jobDate = formatDateAsYearMonthDate(jobDate.clone().subtract(1, 'year'));
    }

    const isDateMatching = isSatelliteLayer(map.sourceLayer)
      ? formatDateAsYearMonthDate(jobDate) === formatDateAsYearMonthDate(mapDate)
      : prescriptionJob.surveyImageId === map.mapSurvey?.surveyImageId;

    const isSimpleDisablingCase =
      (isSatelliteLayer(map.sourceLayer) && map.sourceLayer !== SATELLITE_LAYERS.VITALITY_NDVI) ||
      isYieldLayer(map.sourceLayer) ||
      (isLimeJob && map.sourceLayer !== SURVEY_LAYERS.RT) ||
      (prescriptionJob.layer === map.sourceLayer && isDateMatching) ||
      prescriptionJob.vrmSet?.jobIndex === 1;
    if ((isNonLimeVitalityNdvi || (!isSurveyWithMatchingLayer && isLimeJob)) && isDateMatching) {
      return true;
    }

    return isSimpleDisablingCase;
  };

  const handleYieldReferenceValues = useCallback((mapName, fieldId) => (values) => {
    if (values == null) return;

    const { referenceValues } = mapsState[mapName];
    updateMapState(mapName, 'referenceValues', {
      ...referenceValues,
      [fieldId]: { ...referenceValues[fieldId], [YIELD_LAYERS.YIELD]: values },
    });
  });

  const handleSetShowHeightMapLeft = useCallback((value) => {
    setShowHeightMapLeft(value);
    analytics.logEvent(FIREBASE_EVENTS.VRM_EDITOR_HEIGHT_MAP_LEFT, { shown: value });
  }, []);

  const handleSetShowHeightMapRight = useCallback((value) => {
    setShowHeightMapRight(value);
    analytics.logEvent(FIREBASE_EVENTS.VRM_EDITOR_HEIGHT_MAP_RIGHT, { shown: value });
  }, []);

  const onVariationsEnabledChanged = useCallback(
    (mapName) => (value) => {
      analytics.logEvent(FIREBASE_EVENTS.VRM_EDITOR_SHOW_DETAILS, { shown: value });

      updateMapState(mapName, 'variationsEnabled', value);

      const curr = mapsState[mapName].classificationsEnabled;
      const classificationsEnabled = value ? false : curr;
      updateMapState(mapName, 'classificationsEnabled', classificationsEnabled);
    },
    [mapsState]
  );

  const onClassificationsEnabledChanged = useCallback(
    (mapName) => (value) => {
      analytics.logEvent(FIREBASE_EVENTS.VRM_EDITOR_SHOW_DEMAND, { shown: value });
      setChangeDemandClassification(mapName, value);
    },
    [mapsState, countryCode]
  );

  const handleMultipleSelectionChanged = useCallback(
    (mapName) => (selections) => {
      updateMapState(mapName, 'multipleSelections', selections);
      if (selections.length > 0) {
        analytics.logEvent(FIREBASE_EVENTS.VRM_EDITOR_MULTIPLE_SELECT, {
          selections: selections.join(', '),
        });
      }
    },
    [prescriptionJob, LangFile]
  );

  const drawPrescriptionForJob = useCallback(
    (
      context,
      job,
      drawBounds,
      fieldHoles = [],
      classificationValues,
      shouldUseClassification = false
    ) => {
      const values = job.values;
      const intervals = prescriptionJob.intervals;
      const height = values.length;
      const width = values[0].length;
      const imageData = context.createImageData(width, height);
      const holePolygons = fieldHoles.map((hole) => turf.polygon([hole]));
      const isInsideHole = (x, y, grid) => {
        if (holePolygons.length === 0) return false;
        const { lat, lng } = canvasToMap(y, x, grid, drawBounds);
        const point = turf.point([lng, lat]);
        return holePolygons.some((hole) => turf.booleanContains(hole, point));
      };
      if (
        shouldUseClassification &&
        classificationValues?.mappings?.FINE &&
        classificationValues?.values
      ) {
        const fineMappings = classificationValues.mappings.FINE;
        const classificationRawValues = classificationValues.values;
        const colorsByValue = new Map();
        Object.entries(fineMappings).forEach(([classification, mappedValues]) => {
          const index = intervals.findIndex(
            (interval) => interval.classification === classification
          );
          const color = getIntervalColorForClassificationIndex(index);
          if (color) mappedValues.forEach((value) => colorsByValue.set(value, color));
        });

        iterateRawValues(classificationRawValues, (x, y, value) => {
          if (isInsideHole(x, y, classificationRawValues)) return;
          const color = colorsByValue.get(value);
          if (color) {
            setPixel(imageData, x, y, color);
          }
        });
      } else {
        const hasNullValueInterval = intervals.some(isNullInterval);
        const intervalColors = intervals.map((interval, index) =>
          tinycolor(getPrescriptionIntervalColor(interval, index, intervals)).toRgb()
        );
        iterateRawValues(values, (x, y, value) => {
          if (!(value > 0 || (value === NULL_INTERVAL_VALUE && hasNullValueInterval))) return;
          if (isInsideHole(x, y, values)) return;

          const color = getIntervalColorFromPixel(value, intervals, intervalColors);
          if (color == null) {
            return;
          }
          setPixel(imageData, x, y, color);
        });
      }

      context.putImageData(imageData, 0, 0, 0, 0, width, height);
    },
    [prescriptionJob.intervals]
  );

  const drawOverridesForJob = useCallback((context, job) => {
    const overrides = job.overrides;
    const groups = job.overrideAreas;

    const height = overrides.length;
    const width = overrides[0].length;

    const imageData = context.createImageData(width, height);

    iterateValidOverrides(overrides, (x, y, value) => {
      const group = groups[value];

      if (group) {
        const color = tinycolor(group.color).toRgb();
        setPixel(imageData, x, y, color);
      }
    });

    context.putImageData(imageData, 0, 0, 0, 0, width, height);
  }, []);

  const drawPrescription = useCallback(
    (context: CanvasRenderingContext2D) => {
      drawPrescriptionForJob(
        context,
        prescriptionJob,
        prescriptionJobBoundsRef.current,
        visibleFieldHoles,
        prescriptionJob.classifications,
        layerCapabilities.enableCustomClassification && isDemandLayerType(prescriptionJob.layerType)
      );
    },
    [prescriptionJob, layerCapabilities.enableCustomClassification, drawPrescriptionForJob]
  );

  const drawPrescriptionForBulkFields = useCallback(
    (context: CanvasRenderingContext2D, bulkFieldJob, bulkFieldHoles) => {
      drawPrescriptionForJob(
        context,
        bulkFieldJob,
        bulkFieldJob.bounds,
        bulkFieldHoles,
        bulkFieldJob.classifications,
        layerCapabilities.enableCustomClassification && isDemandLayerType(prescriptionJob.layerType)
      );
    },
    [prescriptionJob, layerCapabilities.enableCustomClassification, drawPrescriptionForJob]
  );

  const drawOverrides = useCallback(
    (context: CanvasRenderingContext2D) => {
      drawOverridesForJob(context, prescriptionJob);
    },
    [prescriptionJob.overrides, prescriptionJob.overrideAreas, drawOverridesForJob]
  );

  const getContainedPixels = useCallback(
    (boxPoly) => {
      return getBrushablePixels(
        prescriptionJob.values,
        activeField.bounds,
        activeField.polygon.coordinates,
        boxPoly
      );
    },
    [prescriptionJob.values, activeField]
  );

  const onOverridesCanvasClicked = useCallback(
    (pixelXY, latLng, boxPoly) => {
      if (
        !turf.booleanPointInPolygon(
          turf.point([latLng.lng, latLng.lat]),
          turf.polygon(jobPolygon.current.coordinates)
        )
      )
        return;
      const nextOverrides = prescriptionJob.overrides.map((row) => row.slice(0));
      const containedPixels = getContainedPixels(boxPoly);

      for (const { x, y } of containedPixels) {
        nextOverrides[y][x] = overrideBrushValue;
      }

      onOverridesChanged(nextOverrides);
    },
    [onOverridesChanged, overrideBrushValue, prescriptionJob.overrides, getContainedPixels]
  );

  const onOverridesCanvasHovered = useCallback(
    (boxPoly, ctx, width, height, latLng) => {
      const prevFill = ctx.fillStyle;
      ctx.clearRect(0, 0, width, height);
      if (
        !turf.booleanPointInPolygon(
          turf.point([latLng.lng, latLng.lat]),
          turf.polygon(jobPolygon.current.coordinates)
        )
      )
        return;
      const containedPixels = getContainedPixels(boxPoly);
      ctx.fillStyle = 'rgba(35,35,200,0.2)';
      for (const { x, y } of containedPixels) {
        ctx.fillRect(x, y, 1, 1);
      }
      ctx.fillStyle = prevFill;
    },
    [getContainedPixels]
  );

  const hasNullInterval = prescriptionJob.intervals.some(isNullInterval);
  const intervalBoundaryKey = useMemo(
    () => getIntervalBoundaryKey(prescriptionJob.intervals),
    [prescriptionJob.intervals]
  );
  const nullZonesForButton = useMemo(() => {
    if (isDemandLayerType(prescriptionJob.layerType) || isOnBulkOverview) return null;
    const polygon = turf.polygon(activeField.polygon.coordinates);
    const values = restoreNullValues(prescriptionJob.values);
    return getNullValueRegions(values, (x, y) =>
      isCellInsideField(x, y, values, activeField.bounds, polygon)
    );
  }, [prescriptionJob.values, prescriptionJob.layerType, activeField, isOnBulkOverview]);
  const nullValueRegions = useMemo(() => {
    if (isOverriding || isOnBulkOverview || hasNullInterval) return null;
    const polygon = turf.polygon(activeField.polygon.coordinates);
    return getNullValueRegions(prescriptionJob.values, (x, y) =>
      isCellInsideField(x, y, prescriptionJob.values, activeField.bounds, polygon)
    );
  }, [prescriptionJob.values, activeField, isOverriding, isOnBulkOverview, hasNullInterval]);
  const [nullPopupPosition, setNullPopupPosition] = useState(null);
  const hoveredNullRegion = useRef(-1);

  const onNullValueHovered = useCallback(
    (point, latLng, ctx) => {
      const row = nullValueRegions && nullValueRegions.regionIds[point.y];
      const insideField = turf.booleanPointInPolygon(
        turf.point([latLng.lng, latLng.lat]),
        turf.polygon(activeField.polygon.coordinates)
      );
      const id = insideField && row && row[point.x] !== undefined ? row[point.x] : -1;
      if (id === hoveredNullRegion.current) return;
      hoveredNullRegion.current = id;
      ctx.clearRect(0, 0, prescriptionJob.values[0].length, prescriptionJob.values.length);
      if (id < 0) return;
      ctx.fillStyle = 'rgba(35, 100, 240, 0.45)';
      nullValueRegions.regions[id].forEach(({ x, y }) => ctx.fillRect(x, y, 1, 1));
    },
    [nullValueRegions, activeField, prescriptionJob.values]
  );

  const onNullValueLeave = useCallback(() => {
    hoveredNullRegion.current = -1;
  }, []);

  const onNullValueClicked = useCallback(
    (point, latLng) => {
      if (isBulkPrescription && !hasSignificantNullZonesInField(prescriptionJob, activeField))
        return;
      const row = nullValueRegions && nullValueRegions.regionIds[point.y];
      const insideField = turf.booleanPointInPolygon(
        turf.point([latLng.lng, latLng.lat]),
        turf.polygon(activeField.polygon.coordinates)
      );
      const id = insideField && row && row[point.x] !== undefined ? row[point.x] : -1;
      setNullPopupPosition(id >= 0 ? latLng : null);
    },
    [nullValueRegions, activeField, isBulkPrescription, prescriptionJob]
  );

  useEffect(() => {
    hoveredNullRegion.current = -1;
    setNullPopupPosition(null);
  }, [prescriptionJob.fieldId, prescriptionJob.values, isOnBulkOverview]);

  const setCurrentNullInterval = useCallback(
    (enabled) => {
      if (
        enabled &&
        isBulkPrescription &&
        !hasSignificantNullZonesInField(prescriptionJob, activeField)
      )
        return;
      const nextJob = setNullIntervalForJob(prescriptionJob, activeField, enabled);
      if (nextJob === prescriptionJob) return;
      updatePrescriptionJob({ values: nextJob.values, intervals: nextJob.intervals }, true);
      if (isBulkPrescription && !isOnBulkOverview) {
        dispatch(setBulkPrescriptionJob(prescriptionJob.fieldId, nextJob));
      }
      setNullPopupPosition(null);
    },
    [
      prescriptionJob,
      activeField,
      updatePrescriptionJob,
      isBulkPrescription,
      isOnBulkOverview,
      dispatch,
    ]
  );

  const addNullInterval = useCallback(() => setCurrentNullInterval(true), [setCurrentNullInterval]);

  const setBulkNullInterval = useCallback(
    (enabled) => {
      const jobs = { ...bulkPrescriptionJobs };
      Object.keys(jobs).forEach((fieldId) => {
        const job = jobs[fieldId];
        const bulkField = bulkPrescriptionFields.find((item) => String(item.fieldId) === fieldId);
        if (!bulkField || isDemandLayerType(job.layerType)) return;
        jobs[fieldId] = setNullIntervalForJob(
          job,
          bulkField,
          enabled && hasSignificantNullZonesInField(job, bulkField)
        );
      });
      dispatch(setBulkPrescriptionJobs(jobs));
      updatePrescriptionJob(
        {
          intervals: setNullIntervalInIntervals(prescriptionJob.intervals, enabled),
        },
        false
      );
    },
    [
      bulkPrescriptionJobs,
      bulkPrescriptionFields,
      prescriptionJob.intervals,
      dispatch,
      updatePrescriptionJob,
    ]
  );

  const setBulkNullIntervalRef = useRef(setBulkNullInterval);
  setBulkNullIntervalRef.current = setBulkNullInterval;
  const addBulkNullInterval = useCallback(() => setBulkNullIntervalRef.current(true), []);

  const LayerCalculationTooltip = ({ prescriptionJob }) => {
    return (
      <Tooltip title={LangFile.PrescriptionJob.layerCalculationBasedOn}>
        <Box sx={{ paddingTop: '4px' }}>
          <Button style={{ fontSize: 'x-small', pointerEvents: 'none' }} variant={'contained'}>
            <Box
              sx={{
                display: 'flex',
                mt: 0.5,
                mb: 0.5,
              }}
            >
              <Typography style={{ fontSize: 'x-small' }}>
                {isSatelliteLayer(prescriptionJob.layer)
                  ? LangFile.PrescriptionJob.satelliteLayers.vitality
                  : LangFile.SurveyLayers[prescriptionJob.layer].name}
              </Typography>
            </Box>
          </Button>
        </Box>
      </Tooltip>
    );
  };

  const renderMapDateHandler = (mapName) => {
    const { selectedDate, sourceLayer } = mapsState[mapName];

    // Yield keeps its own date selector in bulk, because each field has its own harvest dates.
    if (jobBasedOnOldField || (isBulkPrescription && !isYieldLayer(sourceLayer))) {
      return (
        <>
          <DateHandler dates={[prescriptionJob.date]} date={prescriptionJob.date} />
        </>
      );
    }
    if (isSatelliteLayer(sourceLayer)) {
      return (
        <>
          <DateHandler
            dates={datesWithImages}
            date={
              datesWithImages.includes(selectedDate)
                ? selectedDate
                : datesWithImages[datesWithImages.length - 1]
            }
            handleDateSet={(value) => {
              const normalizedDate = formatDateAsYearMonthDate(value);
              if (datesWithImages.includes(normalizedDate)) {
                updateMapState(mapName, 'selectedDate', normalizedDate);
              }
            }}
          />
        </>
      );
    }
    if (isSurveyLayer(sourceLayer)) {
      const selectedSurveyLayerDates = storeSurveysRef.current
        .filter(
          (survey) =>
            Object.keys(survey.newImages).includes(sourceLayer) &&
            survey.fieldId === field.current.fieldId
        )
        .map((survey) => formatDateAsYearMonthDateTime(survey.publishDate))
        .sort((a, b) => new Date(a) - new Date(b));
      return (
        <>
          <DateHandler
            dates={selectedSurveyLayerDates}
            date={
              selectedSurveyLayerDates.includes(selectedDate)
                ? selectedDate
                : selectedSurveyLayerDates[selectedSurveyLayerDates.length - 1]
            }
            handleDateSet={(value) => {
              const normalizedDate = formatDateAsYearMonthDate(value);
              const matchedSurveyDate = selectedSurveyLayerDates.find(
                (surveyDate) => formatDateAsYearMonthDate(surveyDate) === normalizedDate
              );
              if (matchedSurveyDate) {
                updateMapState(mapName, 'selectedDate', matchedSurveyDate);
              }
            }}
          />
        </>
      );
    }
    if (isYieldLayer(sourceLayer) && prescriptionJob.assets[YIELD_LAYERS.YIELD]) {
      return (
        <YieldSelector
          key={prescriptionJob.fieldId}
          setImageURL={(value) => updateMapState(mapName, 'imageUrl', value)}
          setVariableColorScaleObject={setVariableColorScaleObject}
          setReferenceValues={handleYieldReferenceValues(mapName, prescriptionJob.fieldId)}
          dispatch={dispatch}
        />
      );
    }
  };

  const classificationValues = useCallback(
    (fieldId, map) => {
      const classifications =
        bulkDemandClassificationsByLayer[map.sourceLayer]?.[fieldId]?.classifications ||
        selectedSurveyClassificationValues[map.sourceLayer] ||
        bulkPrescriptionJobs[fieldId]?.classifications ||
        (prescriptionJob.fieldId === fieldId ? prescriptionJob.classifications : undefined);
      return { [map.sourceLayer]: classifications };
    },
    [
      bulkDemandClassificationsByLayer,
      bulkPrescriptionJobs,
      selectedSurveyClassificationValues,
      prescriptionJob,
    ]
  );

  const tooltipValues = useCallback(
    (fieldId, mapName) => {
      const map = mapsState[mapName];
      const aux = map.classificationsEnabled ? auxValues[mapName] || {} : {};

      if (map.referenceValues && map.referenceValues[fieldId]) {
        const refValues = map.referenceValues[fieldId];
        return { ...refValues, ...aux };
      } else if (map.multipleSelections.length > 0) {
        return map.referenceValues;
      }
      return { [map.sourceLayer]: [] };
    },
    [mapsState, auxValues]
  );

  const imageUrl = useCallback(
    (map) => {
      if (isSatelliteLayer(map.sourceLayer)) {
        return getPrescriptionSatelliteImageUrl(map, prescriptionJob.assets);
      }
      if (isSurveyLayer(map.sourceLayer)) {
        // Prefer the date-aware URL computed by handleLayer. It reflects the
        // survey matching the selectedDate as well as classificationsEnabled and
        // variationsEnabled. prescriptionJob.assets is a static snapshot from
        // the survey the job was built on (typically the latest), so using it
        // for demand on an older survey renders the wrong image (or none, when
        // the latest survey has no demand image).
        if (map.imageUrl) return map.imageUrl;

        // First-paint fallback before handleLayer has run. Only fall back for
        // raw / normalized - never for demand, since the snapshot is exactly
        // what's broken for demand on non-latest surveys.
        const variationsEnabled =
          map.variationsEnabled && !isSoilLayer(map.sourceLayer) && !map.classificationsEnabled;
        const imageKey = variationsEnabled ? NORMALIZED_IMAGES_KEY : RAW_IMAGES_KEY;
        return prescriptionJob.assets[map.sourceLayer]?.[imageKey];
      }

      return map.imageUrl;
    },
    [prescriptionJob.assets]
  );

  const renderMapAndMapCanvas = (mapName, syncedMap) => {
    const map = mapsState[mapName];

    const legendRef = mapName === 'leftMap' ? legendRefLeftMap : legendRefCenterMap;
    const showSelectedMapTag =
      selectedMap === map.identifier && (mapName === 'centerMap' || numberOfAddedViews > 0);
    const variationsEnabled =
      map.variationsEnabled && !isSoilLayer(map.sourceLayer) && !map.classificationsEnabled;

    const enableSurveyForField = (!isBulkPrescription || appliedInitialIntervals) && enableSurveys;
    const enableNdviForField = (!isBulkPrescription || appliedInitialIntervals) && enableNdvi;
    const enableYieldForField = (!isBulkPrescription || appliedInitialIntervals) && enableYield;

    return (
      <Fragment>
        <MapFloatPanel zIndex={0} top={0} left={0} right={0} bottom={0}>
          <GoogleMap mapType={'transparent'} disableInteraction={true} syncWithMap={syncedMap}>
            <HeightMap shown={showHeightMapLeft} overlays={overlays} />
          </GoogleMap>
        </MapFloatPanel>

        <MapFloatPanel zIndex={200} top={8} left={8}>
          <MapLegend
            viewMode={viewMode}
            showSatelliteImagery={isSatelliteLayer(map.sourceLayer)}
            showSoilSurveys={isSurveyLayer(map.sourceLayer)}
            showOtherLayers={isYieldLayer(map.sourceLayer)}
            selectedFieldHasImage={enableNdviForField || isSatelliteLayer(map.sourceLayer)}
            selectedFieldHasSurvey={enableSurveyForField || isSurveyLayer(map.sourceLayer)}
            selectedLayer={map.sourceLayer}
            surveys={jobBasedOnOldField ? null : storeSurveysRef.current}
            values={getPrescriptionLegendValues(
              map.referenceValues,
              activeField.fieldId,
              map.sourceLayer
            )}
            dates={selectedFieldDates}
            renderDateHandler={() => renderMapDateHandler(mapName)}
            networkEnabled={false}
            variationsEnabled={variationsEnabled}
            classificationsEnabled={map.classificationsEnabled}
            surveyClassificationValues={selectedSurveyClassificationValues}
            onSetSatelliteImageType={(type) => updateMapState(mapName, 'sourceLayer', type)}
            onSetSurveyType={(type) => updateMapState(mapName, 'sourceLayer', type)}
            onVariationsEnabledChanged={onVariationsEnabledChanged(mapName)}
            onClassificationsEnabledChanged={onClassificationsEnabledChanged(
              mapName,
              'classificationsEnabled'
            )}
            enableMultipleSelect={true}
            multipleSelections={map.multipleSelections}
            onMultipleSelectChanged={handleMultipleSelectionChanged(mapName)}
            disableDemandClassification={disableClassificationToggle}
            enableYield={enableYieldForField}
            selectedFieldHasYield={!!prescriptionJob.assets[YIELD_LAYERS.YIELD]}
            onSelectOtherType={(type) => updateMapState(mapName, 'sourceLayer', type)}
            renderVariableColorScale={() => {
              if (isYieldLayer(map.sourceLayer)) {
                return (
                  <VariableColorScale
                    tooltip={LangFile.MapLegend.yieldScaleTooltip}
                    variableColorScaleObject={variableColorScaleObject}
                    unit={LangFile.MapLegend.yieldUnit}
                  />
                );
              }
            }}
            mapSurvey={map.mapSurvey}
          />

          <Box
            ref={legendRef}
            style={{ pointerEvents: 'none' }}
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          />
        </MapFloatPanel>

        <MapFloatPanel zIndex={200} top={8} right={8}>
          <HeightMapToggle
            onToggle={handleSetShowHeightMapLeft}
            showHeightMap={showHeightMapLeft}
          />
        </MapFloatPanel>

        <MapFloatPanel zIndex={2} top={8} right={8}>
          {showSelectedMapTag && (
            <Box>
              <Button style={{ fontSize: 'x-small', pointerEvents: 'none' }} variant={'contained'}>
                <Box
                  sx={{
                    display: 'flex',
                    mt: 0.5,
                    mb: 0.5,
                  }}
                >
                  <Typography style={{ fontSize: 'x-small' }}>
                    {LangFile.PrescriptionSplitViewContainer.selectedMap}
                  </Typography>
                </Box>
              </Button>
            </Box>
          )}
        </MapFloatPanel>

        {isOnBulkOverview ? (
          bulkPrescriptionFields.map((bulkField) => {
            const bulkFieldJob = bulkPrescriptionJobs[bulkField.fieldId];
            const bulkJobSurvey = storeSurveys.find(
              (s) =>
                s.fieldId === bulkField.fieldId && s.surveyImageId === bulkFieldJob.surveyImageId
            );

            let url;
            if (isSurveyLayer(map.sourceLayer) && bulkJobSurvey) {
              const config = getLayerConfig(map.sourceLayer);
              url = config.getImageUrl(
                bulkJobSurvey.newImages,
                map.classificationsEnabled,
                variationsEnabled,
                ViewModeConstants.PRESCRIPTION
              );
            } else if (isSatelliteLayer(map.sourceLayer)) {
              url = bulkField.imageUrl[map.sourceLayer];
            }

            return (
              <Fragment key={`bulk-${mapName}-${bulkField.fieldId}`}>
                <MapCanvasMiniatureContainer
                  viewMode={viewMode}
                  legendRef={legendRef.current}
                  isOverriding={isOverriding}
                  imageURL={url}
                  field={{ ...bulkField }}
                  selectedLayer={map.sourceLayer}
                  multipleSelections={map.multipleSelections}
                  hideReferenceMaps={isBulkPrescription && !appliedInitialIntervals}
                  classificationsEnabled={map.classificationsEnabled}
                  onMultipleSelectChanged={handleMultipleSelectionChanged(mapName)}
                  variationsEnabled={variationsEnabled}
                  values={tooltipValues(bulkField.fieldId, mapName)}
                  classificationValues={classificationValues(bulkField.fieldId, map)}
                  selectedDate={
                    bulkJobSurvey
                      ? formatDateAsYearMonthDateTime(bulkJobSurvey.publishDate)
                      : map.selectedDate
                  }
                  mapSurvey={bulkJobSurvey}
                  surveysRef={storeSurveysRef.current}
                />

                <MapCanvas
                  zIndex={2}
                  clipPathPolygon={bulkField.polygon.coordinates[0]}
                  updateDependencies={[drawOverrides]}
                  bounds={bulkFieldJob.bounds}
                  width={bulkFieldJob.values[0].length}
                  height={bulkFieldJob.values.length}
                  onDraw={drawOverrides}
                />

                {visibleFieldHoles &&
                  visibleFieldHoles.map((boundary, index) => (
                    <MapFieldHole
                      fieldHole={boundary}
                      key={`${mapName}-hole-${bulkField.fieldId}-${index}`}
                      dispatch={dispatch}
                      fieldHoleDisabled={true}
                    />
                  ))}
              </Fragment>
            );
          })
        ) : (
          <>
            <MapCanvasMiniatureContainer
              viewMode={viewMode}
              legendRef={legendRef.current}
              isOverriding={isOverriding}
              imageURL={imageUrl(map)}
              field={activeField}
              selectedLayer={map.sourceLayer}
              multipleSelections={map.multipleSelections}
              classificationsEnabled={map.classificationsEnabled}
              onMultipleSelectChanged={handleMultipleSelectionChanged(mapName)}
              variationsEnabled={variationsEnabled}
              values={tooltipValues(field.current.fieldId, mapName)}
              classificationValues={classificationValues(field.current.fieldId, map)}
              selectedDate={map.selectedDate}
              mapSurvey={map.mapSurvey}
              surveysRef={storeSurveysRef.current}
            />

            <MapCanvas
              zIndex={2}
              clipPathPolygon={jobPolygon.current.coordinates[0]}
              updateDependencies={[drawOverrides]}
              bounds={prescriptionJobBoundsRef.current}
              width={canvasSize.width}
              height={canvasSize.height}
              onDraw={drawOverrides}
            />

            {isOverriding && (
              <MapCanvasClickable
                key={`${mapName}-override-${prescriptionJob.fieldId}`}
                zIndex={6}
                shouldScaleLatitude={shouldScaleLatitude}
                clipPathPolygon={jobPolygon.current.coordinates[0]}
                bounds={prescriptionJobBoundsRef.current}
                width={prescriptionJob.values[0].length}
                height={prescriptionJob.values.length}
                overrideBrushSize={overrideBrushSize}
                clickPolygon={clickPolygon}
                onClick={onOverridesCanvasClicked}
                onHover={onOverridesCanvasHovered}
              />
            )}
            {visibleFieldHoles &&
              visibleFieldHoles.map((boundary, index) => (
                <MapFieldHole
                  fieldHole={boundary}
                  key={`${mapName}-hole-${index}`}
                  dispatch={dispatch}
                  fieldHoleDisabled={true}
                />
              ))}
          </>
        )}
      </Fragment>
    );
  };

  useEffect(() => {
    const isDemand = isDemandLayerType(prescriptionJob.layerType);

    if (shouldInitializeMapSelection)
      updateMapsState({
        leftMap: {
          sourceLayer: prescriptionJob.layer,
          classificationsEnabled: isDemand,
          variationsEnabled: !isDemand,
          multipleSelections:
            prescriptionJobStrategy?.miniatureMapSelection ||
            metaTypeToInitialLayerSelection[prescriptionJob.metaType],
        },
        centerMap: {
          sourceLayer: prescriptionJob.layer,
          classificationsEnabled: isDemand,
          variationsEnabled: !isDemand,
        },
      });

    if (prescriptionJob.layerValues) {
      const sourceValues =
        prescriptionJob.metaType === MetaTypes.LIME
          ? prescriptionJob.layerValues[prescriptionJob.fieldId] || prescriptionJob.layerValues
          : prescriptionJob.layerValues;
      const centerValuesKey =
        prescriptionJob.metaType === MetaTypes.LIME
          ? prescriptionJob.fieldId
          : prescriptionJob.layer;
      updateMapState('leftMap', 'referenceValues', {
        ...mapsState.leftMap.referenceValues,
        [prescriptionJob.fieldId]: sourceValues,
      });
      updateMapState('centerMap', 'referenceValues', {
        ...mapsState.centerMap.referenceValues,
        [centerValuesKey]: sourceValues,
      });
    } else {
      // A lime job's values are kg/ha, not measured Rt. Older jobs without
      // source grids must fetch those grids instead of displaying lime as Rt.
      const initialLayerValues =
        prescriptionJob.metaType === MetaTypes.LIME
          ? {}
          : { [prescriptionJob.layer]: prescriptionJob.values };

      updateMapState('leftMap', 'referenceValues', {
        ...mapsState.leftMap.referenceValues,
        [prescriptionJob.fieldId]: initialLayerValues,
      });
      updateMapState('centerMap', 'referenceValues', {
        ...mapsState.centerMap.referenceValues,
        ...(prescriptionJob.metaType === MetaTypes.LIME
          ? { [prescriptionJob.fieldId]: initialLayerValues }
          : initialLayerValues),
      });
    }

    setInitializationCompleted(true);
  }, [prescriptionJob.layerValues, prescriptionJobStrategy, mapSelectionInitializationKey]);

  useEffect(() => {
    debouncedUpdateLeftMap(mapsState.leftMap, prescriptionJob, surveysForSelectedFields);

    // Clean up: cancel pending debounce if dependencies change or on unmount
    return () => debouncedUpdateLeftMap.cancel();
  }, [
    mapsState.leftMap.sourceLayer,
    mapsState.leftMap.classificationsEnabled,
    mapsState.leftMap.variationsEnabled,
    mapsState.leftMap.selectedDate,
    mapsState.leftMap.multipleSelections,
  ]);

  useEffect(() => {
    if (numberOfAddedViews < 1) return;

    debouncedUpdateCenterMap(mapsState.centerMap, prescriptionJob, surveysForSelectedFields);

    // Cancel pending debounce if dependencies change or on unmount
    return () => debouncedUpdateCenterMap.cancel();
  }, [
    mapsState.centerMap.sourceLayer,
    mapsState.centerMap.classificationsEnabled,
    mapsState.centerMap.variationsEnabled,
    mapsState.centerMap.selectedDate,
    mapsState.centerMap.multipleSelections,
    numberOfAddedViews,
  ]);

  // When the source layer or demand classification changes, swap out stale
  // miniatures and inject the ones for the current layer. Also re-runs when
  // multipleSelections itself changes, as a safety net against other effects
  // that reset it (e.g. metaType/layer transitions) while demand is enabled.
  useEffect(() => {
    ['leftMap', 'centerMap'].forEach((mapName) => {
      const map = mapsState[mapName];
      if (map.classificationsEnabled && map.sourceLayer) {
        syncDemandMiniatures(mapName, map);
      }
    });
  }, [
    countryCode,
    mapsState.leftMap.sourceLayer,
    mapsState.leftMap.classificationsEnabled,
    mapsState.leftMap.multipleSelections,
    mapsState.centerMap.sourceLayer,
    mapsState.centerMap.classificationsEnabled,
    mapsState.centerMap.multipleSelections,
  ]);

  // Fetch DK RT auxiliary values (clay + humus) when the relevant map state changes.
  useEffect(() => {
    ['leftMap', 'centerMap'].forEach((mapName) => {
      const map = mapsState[mapName];
      const eligibleForDkRtAux =
        countryCode === 'DK' &&
        map.sourceLayer === SURVEY_LAYERS.RT &&
        map.classificationsEnabled &&
        map.mapSurvey;
      if (!eligibleForDkRtAux) return;

      getDkRtAuxiliaryValuesPromise(map.selectedDate, map.mapSurvey).then((aux) => {
        if (Object.keys(aux).length === 0) return;
        setAuxValues((prev) => ({
          ...prev,
          [mapName]: { ...prev[mapName], ...aux },
        }));
      });
    });
  }, [
    countryCode,
    mapsState.leftMap.sourceLayer,
    mapsState.leftMap.classificationsEnabled,
    mapsState.leftMap.selectedDate,
    mapsState.leftMap.mapSurvey,
    mapsState.centerMap.sourceLayer,
    mapsState.centerMap.classificationsEnabled,
    mapsState.centerMap.selectedDate,
    mapsState.centerMap.mapSurvey,
  ]);

  useEffect(() => {
    if (!mapsState.leftMap.sourceLayer || !initializationCompleted || !shouldShowCurrentJob) return;

    let baselineSelections = metaTypeToInitialLayerSelection[prescriptionJob.metaType] || [];

    if (prescriptionJobStrategy) {
      baselineSelections = prescriptionJobStrategy.miniatureMapSelection;
    }

    const buildMultipleSelections = (map) => {
      if (!map.classificationsEnabled) return baselineSelections;

      const demandMiniatures = getDefaultDemandMiniatures(prescriptionJob.layer, countryCode);
      const toAdd = demandMiniatures.filter((layer) => !baselineSelections.includes(layer));
      return [...baselineSelections, ...toAdd];
    };

    const nextMaps = shouldInitializeMapSelection
      ? {
          leftMap: {
            ...mapsState.leftMap,
            sourceLayer: prescriptionJob.layer,
            multipleSelections: buildMultipleSelections(mapsState.leftMap),
          },
          centerMap: {
            ...mapsState.centerMap,
            sourceLayer: prescriptionJob.layer,
            multipleSelections: buildMultipleSelections(mapsState.centerMap),
          },
        }
      : mapsState;
    if (shouldInitializeMapSelection) updateMapsState(nextMaps);

    // Update field references
    setSourceValues(prescriptionJob.values);
    setCanvasSize({
      height: prescriptionJob.values.length,
      width: prescriptionJob.values[0].length,
    });

    handleLayer('centerMap', nextMaps.centerMap, prescriptionJob);
    handleLayer('leftMap', nextMaps.leftMap, prescriptionJob);
  }, [prescriptionJob, countryCode, prescriptionJobStrategy]);

  useEffect(() => {
    const bounds = getInitialMapBounds();

    // Fit maps
    if (leftGoogleMapRef.current) fitMapToBounds(leftGoogleMapRef.current, bounds);
    if (centerGoogleMapRef.current) fitMapToBounds(centerGoogleMapRef.current, bounds);
    if (rightGoogleMapRef.current) fitMapToBounds(rightGoogleMapRef.current, bounds);
  }, [prescriptionJob.fieldId]);

  useEffect(() => {
    const bounds = getInitialMapBounds();

    if (leftGoogleMapRef.current) fitMapToBounds(leftGoogleMapRef.current, bounds);
    if (centerGoogleMapRef.current) fitMapToBounds(centerGoogleMapRef.current, bounds);
    if (rightGoogleMapRef.current) fitMapToBounds(rightGoogleMapRef.current, bounds);

    if (initializationCompleted) {
      updateMapsState({
        leftMap: {
          ...mapsState.leftMap,
          sourceLayer: prescriptionJob.layer,
        },
        centerMap: {
          ...mapsState.centerMap,
          sourceLayer: prescriptionJob.layer,
        },
      });
    }
  }, [appliedInitialIntervals]);

  useEffect(() => {
    initializedMapSelection.current = mapSelectionInitializationKey;
    preserveSelectionsOnRecalculation.current = false;
  }, [mapSelectionInitializationKey, prescriptionJob]);

  return (
    <PrescriptionSplitView
      overrideMode={isOverriding}
      overrideBrushValue={overrideBrushValue}
      bounds={getInitialMapBounds()}
      numberOfAddedViews={numberOfAddedViews}
    >
      {(leftMap) => {
        leftGoogleMapRef.current = leftMap;
        return renderMapAndMapCanvas('leftMap', leftMap);
      }}

      {numberOfAddedViews > 0
        ? (centerMap) => {
            centerGoogleMapRef.current = centerMap;
            return renderMapAndMapCanvas('centerMap', centerMap);
          }
        : () => null}

      {(rightMap) => {
        rightGoogleMapRef.current = rightMap;
        return (
          <Fragment>
            {hasMultipleJobs && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  position: 'absolute',
                  zIndex: '1',
                  top: '8px',
                  right: '8px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'white',
                    borderRadius: '4px',
                    padding: '0 8px',
                    marginBottom: '8px',
                  }}
                >
                  <InfoOutlinedIcon style={{ marginRight: '8px' }} fontSize="large" />
                  <p>{LangFile.PrescriptionSplitViewContainer.multipleJobs.info}</p>
                </Box>
                <Box
                  sx={{
                    bgcolor: 'white',
                  }}
                >
                  <Tabs value={tabValue} onChange={(event, newValue) => handleTabChange(newValue)}>
                    <Tab label={LangFile.PrescriptionSplitViewContainer.multipleJobs.currentJob} />
                    <Tab
                      data-testid="future-job-tab"
                      label={LangFile.PrescriptionSplitViewContainer.multipleJobs.futureJob}
                    />
                  </Tabs>
                </Box>
                <LayerCalculationTooltip prescriptionJob={prescriptionJob} />
              </Box>
            )}

            <MapFloatPanel zIndex={200} top={8} right={8}>
              <HeightMapToggle
                onToggle={handleSetShowHeightMapRight}
                showHeightMap={showHeightMapRight}
              />
            </MapFloatPanel>

            <MapFloatPanel zIndex={0} top={0} left={0} right={0} bottom={0}>
              <GoogleMap mapType={'transparent'} disableInteraction={true} syncWithMap={rightMap}>
                <HeightMap shown={showHeightMapRight} overlays={overlays} />
              </GoogleMap>
            </MapFloatPanel>

            <MapFloatPanel zIndex={2} top={8} left={8}>
              <>
                <Box
                  sx={{
                    minWidth: 180,
                    maxWidth: 180,
                  }}
                >
                  <Button
                    style={{ fontSize: 'x-small' }}
                    disabled={recalculatePrescriptionDisabled()}
                    onClick={() => {
                      if (hasMultipleJobs) {
                        handleTabChange(0);
                      }
                      setShowRecalculateDialog(true);
                    }}
                    variant={'contained'}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        mt: 0.5,
                        mb: 0.5,
                      }}
                    >
                      <RefreshIcon fontSize="small" />{' '}
                      <div style={{ marginLeft: '8px' }}>
                        {LangFile.PrescriptionRecalculate.recalculatePrescription}
                      </div>
                    </Box>
                  </Button>
                </Box>
                <PrescriptionRecalculateDialog
                  onCancel={() => setShowRecalculateDialog(false)}
                  open={showRecalculateDialog}
                  onRecalculate={() => {
                    setShowRecalculateDialog(false);
                    recalculatePrescription();
                  }}
                  isBulkPrescription={isBulkPrescription}
                />
              </>
            </MapFloatPanel>

            {!hasMultipleJobs && (
              <MapFloatPanel zIndex={2} top={8} right={8}>
                <LayerCalculationTooltip prescriptionJob={prescriptionJob} />
              </MapFloatPanel>
            )}

            {isOnBulkOverview ? (
              <>
                {bulkPrescriptionFields.map((bulkField) => {
                  const bulkFieldJob = shouldShowCurrentJob
                    ? bulkPrescriptionJobs[bulkField.fieldId]
                    : bulkFuturePrescriptionJobs[bulkField.fieldId];
                  const bulkFieldHoles = bulkField.polygon.coordinates.slice(1);

                  return (
                    <Fragment key={`bulk-right-${bulkField.fieldId}`}>
                      <DataLayer
                        setStyle={(feature) => {
                          return {
                            strokeColor: yellow['A400'],
                            fillOpacity: 0,
                            clickable: false,
                          };
                        }}
                      >
                        <FeaturePolygon coords={bulkField.polygon.coordinates} />
                      </DataLayer>

                      {bulkFieldJob?.values?.[0]?.length > 0 && (
                        <>
                          <MapCanvas
                            redrawOnCallbackChange={false}
                            updateDependencies={[
                              bulkFieldJob.values,
                              bulkFieldJob.classifications,
                              intervalBoundaryKey,
                              layerCapabilities.enableCustomClassification,
                              prescriptionJob.layerType,
                            ]}
                            clipPathPolygon={bulkField.polygon.coordinates[0]}
                            bounds={bulkFieldJob.bounds}
                            width={bulkFieldJob.values[0].length}
                            height={bulkFieldJob.values.length}
                            onDraw={(context) =>
                              drawPrescriptionForBulkFields(context, bulkFieldJob, bulkFieldHoles)
                            }
                          />
                          <MapCanvas
                            redrawOnCallbackChange={false}
                            updateDependencies={[
                              bulkFieldJob.overrides,
                              bulkFieldJob.overrideAreas,
                            ]}
                            clipPathPolygon={bulkField.polygon.coordinates[0]}
                            bounds={bulkFieldJob.bounds}
                            width={bulkFieldJob.values[0].length}
                            height={bulkFieldJob.values.length}
                            onDraw={(context) => drawOverridesForJob(context, bulkFieldJob)}
                          />
                        </>
                      )}

                      {bulkFieldHoles &&
                        bulkFieldHoles.map((boundary, index) => (
                          <MapFieldHole
                            fieldHole={boundary}
                            key={`bulk-hole-right-${bulkField.fieldId}-${index}`}
                            dispatch={dispatch}
                            fieldHoleDisabled={true}
                            opacity={1.0}
                          />
                        ))}
                    </Fragment>
                  );
                })}
                {shouldShowCurrentJob && !isOverriding && (
                  <BulkNullValueCanvas
                    jobs={bulkPrescriptionJobs}
                    fields={bulkPrescriptionFields}
                    onAdd={addBulkNullInterval}
                    label={
                      LangFile.PrescriptionSplitViewContainer.addAsNewInterval ||
                      'Add as a new interval'
                    }
                    highlightFromButton={highlightNullZones}
                  />
                )}
              </>
            ) : (
              <>
                <DataLayer
                  setStyle={(feature) => {
                    return {
                      strokeColor: yellow['A400'],
                      fillOpacity: 0,
                      clickable: false,
                    };
                  }}
                >
                  <FeaturePolygon coords={jobPolygon.current.coordinates} />
                </DataLayer>

                <MapCanvas
                  redrawOnCallbackChange={false}
                  updateDependencies={[
                    prescriptionJob.values,
                    intervalBoundaryKey,
                    prescriptionJob.classifications,
                    layerCapabilities.enableCustomClassification,
                    prescriptionJob.layerType,
                  ]}
                  clipPathPolygon={jobPolygon.current.coordinates[0]}
                  bounds={prescriptionJobBoundsRef.current}
                  width={canvasSize.width}
                  height={canvasSize.height}
                  onDraw={drawPrescription}
                />

                <MapCanvas
                  redrawOnCallbackChange={false}
                  updateDependencies={[prescriptionJob.overrides, prescriptionJob.overrideAreas]}
                  clipPathPolygon={jobPolygon.current.coordinates[0]}
                  bounds={prescriptionJobBoundsRef.current}
                  width={canvasSize.width}
                  height={canvasSize.height}
                  onDraw={drawOverrides}
                />

                {!isOverriding && nullZonesForButton?.regions.length > 0 && (
                  <MapCanvas
                    redrawOnCallbackChange={false}
                    zIndex={7}
                    clipPathPolygon={activeField.polygon.coordinates[0]}
                    bounds={activeField.bounds}
                    width={prescriptionJob.values[0].length}
                    height={prescriptionJob.values.length}
                    updateDependencies={[highlightNullZones, nullZonesForButton]}
                    onDraw={(ctx) => {
                      ctx.clearRect(
                        0,
                        0,
                        prescriptionJob.values[0].length,
                        prescriptionJob.values.length
                      );
                      if (!highlightNullZones) return;
                      ctx.fillStyle = 'rgba(35, 100, 240, 0.45)';
                      nullZonesForButton.regions.forEach((region) =>
                        region.forEach(({ x, y }) => ctx.fillRect(x, y, 1, 1))
                      );
                    }}
                  />
                )}

                {!isOverriding &&
                  !isDemandLayerType(prescriptionJob.layerType) &&
                  !hasNullInterval &&
                  nullValueRegions?.regions.length > 0 && (
                    <>
                      <MapCanvasClickable
                        key={`null-zones-${prescriptionJob.fieldId}`}
                        zIndex={6}
                        clipPathPolygon={activeField.polygon.coordinates[0]}
                        bounds={activeField.bounds}
                        width={prescriptionJob.values[0].length}
                        height={prescriptionJob.values.length}
                        clickPolygon={activeField.polygon.coordinates[0]}
                        onHoverPoint={onNullValueHovered}
                        onClickPoint={onNullValueClicked}
                        onMouseLeave={onNullValueLeave}
                      />
                      {nullPopupPosition && (
                        <MapPopup
                          shown
                          selected
                          clickable
                          position={nullPopupPosition}
                          onClickAway={() => setNullPopupPosition(null)}
                          onClick={addNullInterval}
                        >
                          <Button>
                            {LangFile.PrescriptionSplitViewContainer.addAsNewInterval ||
                              'Add as a new interval'}
                          </Button>
                        </MapPopup>
                      )}
                    </>
                  )}

                {isOverriding && (
                  <Fragment>
                    <MapCanvasClickable
                      key={`right-override-${prescriptionJob.fieldId}`}
                      clipPathPolygon={jobPolygon.current.coordinates[0]}
                      bounds={prescriptionJobBoundsRef.current}
                      shouldScaleLatitude={shouldScaleLatitude}
                      width={prescriptionJob.values[0].length}
                      height={prescriptionJob.values.length}
                      overrideBrushSize={overrideBrushSize}
                      clickPolygon={clickPolygon}
                      onClick={onOverridesCanvasClicked}
                      onHover={onOverridesCanvasHovered}
                    />

                    <MapFloatPanel left={8} top={16}>
                      <PrescriptionOverrideList onOverridesUpdated={onOverridesChanged} />
                    </MapFloatPanel>
                  </Fragment>
                )}
                {visibleFieldHoles &&
                  visibleFieldHoles.map((boundary, index) => (
                    <MapFieldHole
                      fieldHole={boundary}
                      key={index}
                      dispatch={dispatch}
                      fieldHoleDisabled={true}
                      opacity={1.0}
                    />
                  ))}
              </>
            )}
          </Fragment>
        );
      }}
    </PrescriptionSplitView>
  );
};

PrescriptionSplitViewContainer.propTypes = {
  selectedField: PropTypes.object,
  isOverriding: PropTypes.bool,
  enableNdvi: PropTypes.bool,
  enableSurveys: PropTypes.bool,
  onRecalculateBulkOverview: PropTypes.func,
  overrideBrushValue: PropTypes.number,
  overrideBrushSize: PropTypes.number,
  onOverridesChanged: PropTypes.func,
  showCurrentJob: PropTypes.bool,
  updatePrescriptionJob: PropTypes.func,
  highlightNullZones: PropTypes.bool,
};

PrescriptionSplitViewContainer.defaultProps = {};

export default memo(connect(mapStateToProps)(PrescriptionSplitViewContainer));
